import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileToBase64, fmt, API } from "@/lib/api";
import axios from "axios";
import {
  Camera,
  UploadSimple,
  Sparkle,
  ArrowRight,
  CheckCircle,
  Tag,
  X,
  Plus,
  Microphone,
  Stop,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CameraCapture from "@/components/CameraCapture";

const MAX_IMAGES = 6;

export default function AIScan() {
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [images, setImages] = useState([]); // [{base64, preview}]
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [moneda, setMoneda] = useState("MXN");
  const [contexto, setContexto] = useState("");
  const [enhanceOnSave, setEnhanceOnSave] = useState(true);
  const [savingProgress, setSavingProgress] = useState(null); // null | {step, total, message}

  // Voice recording
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recStreamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (recTimerRef.current) clearInterval(recTimerRef.current);
      if (recStreamRef.current) recStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Tu navegador no soporta grabación de audio");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        if (recStreamRef.current) {
          recStreamRef.current.getTracks().forEach((t) => t.stop());
          recStreamRef.current = null;
        }
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size === 0) {
          setTranscribing(false);
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await axios.post(`${API}/voice/transcribe`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data?.text) {
            setContexto((c) => (c ? `${c} ${data.text}` : data.text).slice(0, 500));
            toast.success("Transcripción agregada");
          } else {
            toast.warning("No detecté texto");
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error transcribiendo");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        setRecSeconds((s) => {
          if (s >= 60) {
            stopRecording();
            return s;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const stopRecording = () => {
    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try {
        mr.stop();
      } catch {
        /* ignore */
      }
    }
    setRecording(false);
  };

  const onPickFiles = async (files) => {
    if (!files?.length) return;
    const accepted = [];
    for (const file of files) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
        toast.error(`"${file.name}" no es PNG/JPG/WEBP`);
        continue;
      }
      const b64 = await fileToBase64(file);
      accepted.push({ base64: b64, preview: URL.createObjectURL(file) });
    }
    setImages((prev) => {
      const next = [...prev, ...accepted].slice(0, MAX_IMAGES);
      if (prev.length + accepted.length > MAX_IMAGES) {
        toast.warning(`Máximo ${MAX_IMAGES} fotos`);
      }
      setActiveIdx(Math.max(0, next.length - 1));
      return next;
    });
    setResult(null);
  };

  const addBase64Image = (b64) => {
    // Convert base64 into the same shape as onPickFiles so downstream code works.
    const preview = `data:image/jpeg;base64,${b64}`;
    setImages((prev) => {
      const next = [...prev, { base64: b64, preview }].slice(0, MAX_IMAGES);
      setActiveIdx(Math.max(0, next.length - 1));
      return next;
    });
    setResult(null);
  };

  const removeImage = (idx) => {
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      setActiveIdx((curr) => Math.min(curr, Math.max(0, next.length - 1)));
      return next;
    });
    setResult(null);
  };

  const analyze = async () => {
    if (!images.length) return toast.error("Agrega al menos una foto");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/products/analyze", {
        images_base64: images.map((i) => i.base64),
        moneda,
        contexto: contexto.trim(),
      });
      setResult(data);
      toast.success(`Detectado: ${data.objeto}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al analizar");
    } finally {
      setLoading(false);
    }
  };

  const saveToInventory = async () => {
    if (!result) return;
    const originals = images.map((i) => i.base64);
    let imagesToSave = originals;
    let enhancedDone = false;
    try {
      if (enhanceOnSave && imagesToSave.length > 0) {
        const enhanced = [];
        for (let i = 0; i < imagesToSave.length; i++) {
          setSavingProgress({
            step: i + 1,
            total: imagesToSave.length,
            message: `Mejorando foto ${i + 1}/${imagesToSave.length}…`,
          });
          try {
            const { data } = await api.post("/products/enhance-image", {
              image_base64: imagesToSave[i],
              nombre_producto: result.objeto,
              categoria: result.categoria_sugerida,
            });
            if (data?.image_base64) enhanced.push(data.image_base64);
            else enhanced.push(imagesToSave[i]);
          } catch (e) {
            enhanced.push(imagesToSave[i]);
          }
        }
        imagesToSave = enhanced;
        enhancedDone = true;
      }

      setSavingProgress({ step: 0, total: 0, message: "Guardando en inventario…" });
      await api.post("/products", {
        nombre: result.objeto,
        categoria: result.categoria_sugerida,
        descripcion: result.descripcion,
        para_que_sirve: result.para_que_sirve,
        costo: result.precios.oportunidad,
        precio_venta: Math.round((result.precios.usado_min + result.precios.usado_max) / 2),
        cantidad: 1,
        condicion: result.condicion_estimada,
        imagen_base64: imagesToSave[0] || null,
        imagenes_base64: imagesToSave,
        imagenes_originales_base64: enhancedDone ? originals : [],
        precios_referencia: result.precios,
      });
      toast.success(
        enhancedDone
          ? `Guardado con ${imagesToSave.length} foto(s) catálogo profesional`
          : "Guardado en inventario",
      );
      // Redirect to inventory so the user can verify the new item appears in the list
      setTimeout(() => navigate("/inventario"), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingProgress(null);
    }
  };

  const active = images[activeIdx];

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="scan-page">
      <div className="mb-10">
        <div className="overline text-klein">AI Vision</div>
        <h1 className="h-display text-4xl lg:text-5xl font-black tracking-tighter mt-2">
          Escanea, valúa, decide.
        </h1>
        <p className="text-muted2 mt-3 max-w-2xl">
          Sube hasta {MAX_IMAGES} fotos del mismo objeto desde distintos ángulos. GPT-5.2 las analiza en
          conjunto para reconocer marca, modelo, estado y comparar precios con mayor precisión.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Upload */}
        <div className="lg:col-span-5 border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Paso 1 · Fotos del objeto</div>
            <div className="text-[11px] text-muted2 font-mono">
              {images.length}/{MAX_IMAGES}
            </div>
          </div>

          {/* Main preview */}
          <div
            className="relative aspect-square w-full bg-zinc-50 border border-dashed border-zinc-300 flex items-center justify-center overflow-hidden"
            data-testid="image-drop"
          >
            {active ? (
              <>
                <img src={active.preview} alt="preview" className="w-full h-full object-cover" />
                {loading && (
                  <>
                    <div className="absolute inset-0 bg-klein/10" />
                    <div className="absolute inset-x-0 h-1 bg-klein/70 animate-scan" />
                  </>
                )}
                <button
                  onClick={() => removeImage(activeIdx)}
                  className="absolute top-2 right-2 bg-white border border-zinc-300 p-1 hover:bg-[#FFEBEE] hover:border-[#FF2A00] hover:text-[#FF2A00]"
                  data-testid={`remove-img-${activeIdx}`}
                  title="Eliminar foto"
                >
                  <X size={14} weight="bold" />
                </button>
                <div className="absolute bottom-2 left-2 bg-white/90 border border-zinc-200 px-2 py-0.5 text-[11px] font-mono">
                  Ángulo {activeIdx + 1}
                </div>
              </>
            ) : (
              <div className="text-center text-muted2 px-6">
                <Camera size={36} className="mx-auto mb-3 text-zinc-400" />
                <div className="text-sm">PNG · JPG · WEBP</div>
                <div className="text-xs mt-1">Hasta {MAX_IMAGES} fotos · varios ángulos</div>
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 0 && (
            <div className="mt-3 grid grid-cols-6 gap-1.5">
              {images.map((img, idx) => (
                <button
                  key={`${img?.slice(-16) || "img"}-${idx}`}
                  onClick={() => setActiveIdx(idx)}
                  className={`relative aspect-square overflow-hidden border ${
                    activeIdx === idx ? "border-klein ring-2 ring-klein" : "border-zinc-200"
                  }`}
                  data-testid={`thumb-${idx}`}
                >
                  <img src={img.preview} alt={`ángulo ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
              {images.length < MAX_IMAGES && (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="aspect-square border border-dashed border-zinc-300 flex items-center justify-center text-muted2 hover:bg-zinc-50"
                  data-testid="thumb-add"
                  title="Agregar otra foto"
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            className="hidden"
            onChange={(e) => onPickFiles(Array.from(e.target.files || []))}
            data-testid="file-input"
          />

          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              onClick={() => fileRef.current?.click()}
              className="btn-ghost"
              disabled={images.length >= MAX_IMAGES}
              data-testid="btn-upload"
              data-help="Sube hasta 6 fotos del producto desde tu galería. La primera será la principal — puedes reordenarlas."
            >
              <UploadSimple size={18} /> Subir fotos
            </button>
            <button
              onClick={() => setCameraOpen(true)}
              className="btn-ghost"
              disabled={images.length >= MAX_IMAGES}
              data-testid="btn-capture"
              data-help="Abre la cámara en vivo (frontal o trasera) para tomar una o varias fotos en el momento."
            >
              <Camera size={18} /> Cámara
            </button>
          </div>

          <CameraCapture
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={addBase64Image}
            maxPhotos={MAX_IMAGES}
            currentCount={images.length}
          />

          <div className="mt-4">
            <label className="overline block mb-1.5">Moneda</label>
            <select
              className="input-flat"
              value={moneda}
              onChange={(e) => setMoneda(e.target.value)}
              data-testid="select-moneda"
            >
              <option value="MXN">MXN — Peso mexicano</option>
              <option value="USD">USD — Dólar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="ARS">ARS — Peso argentino</option>
              <option value="COP">COP — Peso colombiano</option>
            </select>
          </div>

          <div className="mt-4">
            <label className="overline block mb-1.5 flex items-center justify-between">
              <span>Contexto adicional (opcional)</span>
              <span className="text-[10px] text-muted2 normal-case tracking-normal">
                Mejora la precisión
              </span>
            </label>
            <div className="relative">
              <textarea
                className="input-flat min-h-[80px] text-sm pr-12"
                placeholder="Ej. iPhone 13 Pro 256GB, batería 89%, caja original, sin rayones."
                value={contexto}
                onChange={(e) => setContexto(e.target.value)}
                maxLength={500}
                disabled={transcribing}
                data-testid="input-contexto"
              />
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                className={`absolute bottom-2 right-2 w-9 h-9 inline-flex items-center justify-center transition-colors ${
                  recording
                    ? "bg-[#FF2A00] text-white animate-pulse"
                    : transcribing
                    ? "bg-zinc-200 text-muted2 cursor-not-allowed"
                    : "bg-klein text-white hover:bg-klein-dark"
                }`}
                title={recording ? "Detener grabación" : "Grabar voz"}
                data-testid="btn-mic"
                data-help="Dicta el contexto en lugar de escribirlo (hasta 60s). Whisper lo transcribe a texto en español."
              >
                {recording ? <Stop size={16} weight="fill" /> : <Microphone size={16} weight="fill" />}
              </button>
            </div>
            <div className="text-[10px] text-muted2 mt-1 flex justify-between font-mono">
              <span>
                {recording ? (
                  <span className="text-[#FF2A00] inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF2A00] animate-pulse" />
                    Grabando · {String(recSeconds).padStart(2, "0")}s / 60s
                  </span>
                ) : transcribing ? (
                  <span className="text-klein">Transcribiendo con Whisper…</span>
                ) : (
                  <span>Toca el micrófono para dictar</span>
                )}
              </span>
              <span>{contexto.length}/500</span>
            </div>
          </div>

          <button
            disabled={!images.length || loading}
            onClick={analyze}
            className="btn-klein w-full mt-4 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="btn-analyze"
            data-help="GPT-5.2 Vision identifica el producto, lo clasifica y te sugiere precios nuevo / usado / oportunidad investigando referencias."
          >
            <Sparkle size={18} weight="fill" />
            {loading
              ? "Analizando con IA…"
              : `Analizar ${images.length || ""} ${images.length === 1 ? "foto" : "fotos"} con IA`.replace(/\s+/g, " ")}
          </button>
        </div>

        {/* Result */}
        <div className="lg:col-span-7 border border-zinc-200 p-6 min-h-[500px]">
          <div className="overline mb-3">Paso 2 · Resultado</div>
          {!result && !loading && (
            <div className="h-full flex items-center justify-center text-center py-20">
              <div className="max-w-sm">
                <Sparkle size={28} className="mx-auto text-zinc-400" />
                <div className="h-display text-xl font-bold mt-3">Aún no hay análisis</div>
                <p className="text-sm text-muted2 mt-2">
                  Sube fotos desde varios ángulos y presiona{" "}
                  <span className="font-medium">Analizar con IA</span>.
                </p>
              </div>
            </div>
          )}
          {loading && (
            <div className="py-20 text-center text-sm text-muted2 font-mono">
              <div className="inline-block px-3 py-1 border border-klein text-klein">
                [ analizando · GPT-5.2 · {images.length} {images.length === 1 ? "imagen" : "imágenes"} ]
              </div>
              <p className="mt-3">Reconociendo objeto · investigando precios · comparando plataformas…</p>
            </div>
          )}

          {result && (
            <div className="animate-fade-in">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h2 className="h-display text-3xl lg:text-4xl font-black tracking-tighter">{result.objeto}</h2>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                    <span className="px-2 py-0.5 bg-klein text-white inline-flex items-center gap-1">
                      <Tag size={12} /> {result.categoria_sugerida}
                    </span>
                    <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200">
                      {result.condicion_estimada}
                    </span>
                    <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200">
                      {images.length} {images.length === 1 ? "foto" : "fotos"} analizadas
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <label
                    className="inline-flex items-center gap-2 cursor-pointer text-xs select-none"
                    data-testid="toggle-enhance"
                    data-help="Si está activo, Gemini Nano Banana convierte cada foto a calidad catálogo (fondo limpio, iluminación pareja) antes de guardarla en inventario."
                  >
                    <input
                      type="checkbox"
                      checked={enhanceOnSave}
                      onChange={(e) => setEnhanceOnSave(e.target.checked)}
                      className="accent-klein w-3.5 h-3.5"
                    />
                    <span className="inline-flex items-center gap-1">
                      <Sparkle size={12} className="text-klein" weight="fill" />
                      Mejorar a foto catálogo
                    </span>
                  </label>
                  <button
                    onClick={saveToInventory}
                    disabled={!!savingProgress}
                    className="btn-klein disabled:opacity-60"
                    data-testid="btn-save-inventory"
                    data-help="Crea el producto en tu inventario con los datos sugeridos por la IA. Después podrás editarlo desde Inventario."
                  >
                    {savingProgress ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {savingProgress.message}
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} weight="fill" /> Guardar en inventario
                      </>
                    )}
                  </button>
                </div>
              </div>

              <p className="text-sm mt-4 leading-relaxed">{result.descripcion}</p>

              <div className="mt-4 p-4 bg-zinc-50 border-l-2 border-klein">
                <div className="overline">Para qué sirve</div>
                <div className="text-sm mt-1">{result.para_que_sirve}</div>
              </div>

              <div className="mt-6">
                <div className="overline mb-2">Comparativa de precios ({result.precios.moneda})</div>
                <div className="border border-zinc-200">
                  <PriceRow
                    label="Nuevo"
                    range={`${fmt(result.precios.nuevo_min, result.precios.moneda)} – ${fmt(
                      result.precios.nuevo_max,
                      result.precios.moneda,
                    )}`}
                  />
                  <PriceRow
                    label="Usado"
                    range={`${fmt(result.precios.usado_min, result.precios.moneda)} – ${fmt(
                      result.precios.usado_max,
                      result.precios.moneda,
                    )}`}
                  />
                  <PriceRow
                    label="Oportunidad"
                    range={fmt(result.precios.oportunidad, result.precios.moneda)}
                    accent
                  />
                </div>
                {result.precios.fuentes?.length > 0 && (
                  <div className="text-xs text-muted2 mt-2">
                    Fuentes: {result.precios.fuentes.join(" · ")}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                <Decision label="Comprar" value={result.recomendacion_compra} icon={<ArrowRight size={16} />} />
                <Decision label="Vender" value={result.recomendacion_venta} icon={<ArrowRight size={16} />} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceRow({ label, range, accent }) {
  return (
    <div
      className={`grid grid-cols-12 items-center px-4 py-3 border-b border-zinc-100 last:border-b-0 ${
        accent ? "bg-[#FFF9E6]" : ""
      }`}
    >
      <div className="col-span-4 overline">{label}</div>
      <div
        className={`col-span-8 text-right h-display text-lg font-bold tabular-nums ${
          accent ? "text-[#B45309]" : ""
        }`}
      >
        {range}
      </div>
    </div>
  );
}

function Decision({ label, value, icon }) {
  return (
    <div className="border border-zinc-200 p-4">
      <div className="flex items-center gap-1 overline">
        {icon} {label}
      </div>
      <div className="text-sm mt-2 leading-relaxed">{value}</div>
    </div>
  );
}
